import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { GridPaginationModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { AI_HUB_DEFAULT_PAGE_SIZE } from "./constants";
import type { TaggingResultRow } from "./constants";
import AiHubFilterBar from "./AiHubFilterBar";
import {
  canFetchTaggingResultsList,
  fetchTaggingResultPage,
  optionLabel,
  type TaggingResultDisplayConcept,
} from "./taggingResultData";
import TaggingResultTable from "./TaggingResultTable";
import { resultLoadErrorMessage } from "./resultLoadError";
import { useAiHubObjectScope } from "./useAiHubObjectScope";

export default function TaggingResultTab() {
  const { showMessage } = useSnackbar();
  const {
    objectType,
    objectSubType,
    conceptCode,
    conceptValue,
    objectId,
    setFilters,
    objectTypeOptions,
    planTypesLoading,
    getObjectSubTypeOptions,
  } = useAiHubObjectScope("taggingResult");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: AI_HUB_DEFAULT_PAGE_SIZE,
  });
  const [rows, setRows] = useState<TaggingResultRow[]>([]);
  const [displayConcepts, setDisplayConcepts] = useState<
    TaggingResultDisplayConcept[]
  >([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const objectSubTypeOptions = getObjectSubTypeOptions(objectType);
  const canFetch = canFetchTaggingResultsList(
    objectType,
    objectSubType,
    objectSubTypeOptions.length,
  );

  const resetPage = useCallback(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, []);

  const handleObjectIdSearchChange = useCallback(
    (value: string) => {
      const next = value.trim();
      if (objectId === next) return;
      setFilters({ objectId: next });
      resetPage();
    },
    [objectId, resetPage, setFilters],
  );

  useEffect(() => {
    let mounted = true;

    if (planTypesLoading) {
      return () => {
        mounted = false;
      };
    }

    if (!canFetch) {
      setRows([]);
      setDisplayConcepts([]);
      setTotal(0);
      setLoading(false);
      setHasLoaded(true);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);

    fetchTaggingResultPage({
      objectType,
      objectSubType,
      conceptCode,
      conceptValue,
      objectId,
      pageIndex: paginationModel.page,
      pageSize: paginationModel.pageSize,
    })
      .then((page) => {
        if (!mounted) return;
        setRows(page.rows);
        setDisplayConcepts(page.displayConcepts);
        setTotal(page.total);
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setRows([]);
        setDisplayConcepts([]);
        setTotal(0);
        showMessage(
          resultLoadErrorMessage(
            err,
            "Failed to load Classification Results.",
          ),
          "error",
        );
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
        setHasLoaded(true);
      });

    return () => {
      mounted = false;
    };
  }, [
    canFetch,
    conceptCode,
    conceptValue,
    objectId,
    objectSubType,
    objectType,
    paginationModel.page,
    paginationModel.pageSize,
    planTypesLoading,
    showMessage,
  ]);

  const showInitialLoading = planTypesLoading || (!hasLoaded && loading);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        flex: 1,
        minHeight: 0,
        mx: 0,
      }}
    >
      <AiHubFilterBar
        objectType={objectType}
        objectSubType={objectSubType}
        conceptCode={conceptCode}
        conceptValue={conceptValue}
        dimensionSource="configurable"
        onObjectTypeChange={(nextObjectType, nextObjectSubType) => {
          setFilters({
            objectType: nextObjectType,
            objectSubType: nextObjectSubType,
            conceptCode: "",
            conceptValue: "",
          });
          resetPage();
        }}
        onObjectSubTypeChange={(nextObjectSubType) => {
          setFilters({
            objectSubType: nextObjectSubType,
            conceptCode: "",
            conceptValue: "",
          });
          resetPage();
        }}
        onConceptCodeChange={(nextConceptCode) => {
          setFilters({ conceptCode: nextConceptCode, conceptValue: "" });
          resetPage();
        }}
        onConceptValueChange={(nextValue) => {
          setFilters({ conceptValue: nextValue });
          resetPage();
        }}
      />
      {showInitialLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 320,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TaggingResultTable
          rows={rows}
          displayConcepts={displayConcepts}
          scopeObjectType={objectType}
          scopeObjectSubType={objectSubType}
          objectTypeLabel={optionLabel(objectTypeOptions, objectType)}
          objectSubTypeLabel={optionLabel(objectSubTypeOptions, objectSubType)}
          rowCount={total}
          selectionResetKey={`${objectType}|${objectSubType}|${conceptCode}|${conceptValue}|${objectId}`}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          objectIdSearch={objectId}
          onObjectIdSearchChange={handleObjectIdSearchChange}
        />
      )}
    </Box>
  );
}
